/**
 * AllocCounters — frame-jitter Phase C iter 2 (2026-05-16).
 *
 * Lightweight call-counter for hot-path attribution. Gated by an explicit
 * `enable()` toggle so the per-call overhead is a single boolean check in
 * production (where the counter is disabled). When enabled, increment is
 * O(1): bare property read + write on a null-proto record, no Map overhead.
 *
 * Usage at suspect sites:
 *   import { AllocCounters } from '@/game/diagnostics/AllocCounters';
 *   AllocCounters.inc('entity.tick');
 *
 * Read via window from a Playwright probe:
 *   window.__ALLOC_COUNTERS__.enable();
 *   // ...run scenarios...
 *   const stats = window.__ALLOC_COUNTERS__.getStats();
 *   window.__ALLOC_COUNTERS__.reset();
 *
 * The bot probe (scripts/vqa-bot-telemetry.mjs) drives this end-to-end.
 *
 * IMPORTANT: never use this for production logic. It's strictly a diagnostic
 * tool. The counters do not persist across page reloads and have no upper
 * bound on cardinality — only instrument a small fixed set of suspect sites.
 */

class AllocCountersImpl {
  private enabled = false;
  // Object.create(null) avoids prototype chain lookups + provides a clean
  // dictionary semantics. Counters are int-valued; never NaN.
  private counters: Record<string, number> = Object.create(null);
  private enabledAt = 0;

  enable(): void {
    this.enabled = true;
    this.enabledAt = Date.now();
  }

  disable(): void {
    this.enabled = false;
  }

  inc(key: string, n: number = 1): void {
    if (!this.enabled) return;
    const prev = this.counters[key] ?? 0;
    this.counters[key] = prev + n;
  }

  getStats(): { enabled: boolean; enabledAt: number; durationMs: number; counters: Record<string, number> } {
    return {
      enabled: this.enabled,
      enabledAt: this.enabledAt,
      durationMs: this.enabled ? Date.now() - this.enabledAt : 0,
      counters: { ...this.counters },
    };
  }

  reset(): void {
    this.counters = Object.create(null);
    this.enabledAt = Date.now();
  }
}

export const AllocCounters = new AllocCountersImpl();

if (typeof window !== 'undefined') {
  (window as unknown as { __ALLOC_COUNTERS__: AllocCountersImpl }).__ALLOC_COUNTERS__ = AllocCounters;
}
